import React, { useState } from 'react';
import { Chip, InputBase, Select, MenuItem, makeStyles, Checkbox, ListItemText, IconButton } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { COLORS, SRC } from './logsConstants';
import SimulationRunSelect from './SimulationRunSelect';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useDispatch } from 'react-redux';
import { handleSnackBar, invokePostService } from '../../../store/global/actions';
import LogTimeFilter from './LogTimeFilter';

const LEVEL_OPTIONS = [
  { title: 'Info', value: 'info' },
  { title: 'Debug High', value: 'debug' },
  { title: 'Debug Medium', value: 'debugm' },
  { title: 'Debug Low', value: 'debugl' },
  { title: 'Unusual', value: 'unusual' },
  { title: 'Warn', value: 'warn' },
  { title: 'Error', value: 'error' },
  { title: 'Trace', value: 'trace' },
];

const LOG_LEVEL = {
  1: "Basic",
  2: "Verbose",
  3: "Detailed",
  4: "Debug"
}
const useStyles = makeStyles(() => ({
    toolbar: {
      padding: '14px 24px',
      borderBottom: `1px solid ${COLORS.border}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
    },
    chip: {
      borderRadius: 6,
      border: `1px solid ${COLORS.borderStrong}`,
      fontSize: '12.5px',
      fontWeight: 500,
      cursor: 'pointer',
      backgroundColor: '#ffffff',
      color: '#2e2e2e !important',
    },
    chipActive: {
      borderColor: COLORS.accent,
      backgroundColor: COLORS.accentBg,
      color: '#1D4ED8 !important',
    },
    dot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: 6,
    },
    searchWrap: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 200,
      border: `1px solid ${COLORS.borderStrong}`,
      borderRadius: 6,
      padding: '2px 12px',
      '&:focus-within': { borderColor: COLORS.accent, boxShadow: `0 0 0 2px ${COLORS.accentBg}` },
    },
    searchInput: {
      fontSize: 13,
      flex: 1,
      marginLeft: 8,
    },
    select: {
      fontSize: '12.5px',
      border: `1px solid ${COLORS.borderStrong}`,
      borderRadius: 6,
      backgroundColor: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      maxWidth: '80px'
    },
    selectInput: {
      padding: '6px 28px 6px 10px',
      fontSize: '12.5px',
      color: COLORS.textPrimary,
      '&:focus': { backgroundColor: 'transparent' },
    },
    menuPaper: {
      maxHeight: 320,
      minWidth: 160,
    },
    menuList: {
      paddingTop: 4,
      paddingBottom: 4,
    },
    menuItem: {
      minHeight: 32,
      padding: '1px',
      fontSize: 13,
    },
    checkbox: {
      padding: 4,
      marginRight: 4,
      '& svg': {
        fontSize: 18,
      },
    },
    listItemText: {
      margin: 0,
      paddingRight: '5px',
      '& .MuiListItemText-primary': {
        fontSize: 13,
      },
    },
    terminateButton: {
      fontSize: '12.5px',
      border: '1px solid #d32f2f',
      borderRadius: 6,
      backgroundColor: '#d32f2f',
      color: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '4px 10px',
      cursor: 'pointer',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: '#b71c1c',
      },
      '&:disabled': {
        backgroundColor: '#e57373',
        borderColor: '#e57373',
        cursor: 'not-allowed',
        opacity: 0.9,
      },
    },
    spinner: {
      width: 11,
      height: 11,
      border: '2px solid rgba(255,255,255,0.5)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      display: 'inline-block',
      animation: '$spin 0.7s linear infinite',
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  }));
  

export default function LogsToolbar({
  activeSources,
  onToggleSource,
  counts,
  search,
  onSearchChange,
  level,
  onLevelChange,
  time,
  onTimeChange,
  showTimeFilter,
  selectedSimulationRun,
  setSelectedSimulationRun,
  onRefresh, 
  refreshSpin,
  isSimulationRunning,
  clearingRuntimeLogs,
  handleClearRuntimeLogs,
  onLogLevelChange,
  logLevel
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [terminatingSimulation, setTerminatingSimulation] = useState(false);

  const handleTerminateSimulation = () => {
    setTerminatingSimulation(true);
    dispatch(invokePostService('terminateSimulation', { batch: selectedSimulationRun }, [], true))
      .then((response) => {
          if (response.status && response.data === 0) {
              dispatch(handleSnackBar({ open: true, message: "Simulation terminated", severity: 'success' }));
          }
          setTerminatingSimulation(false);
      })
  }


  return (
    <div className={classes.toolbar}>
      <div className={classes.row}>
        {Object.keys(SRC).map((s) => (
          <Chip
            key={s}
            onClick={() => onToggleSource(s)}
            className={`${classes.chip} tab-${s.toLowerCase().split(' ').join('-')} ${activeSources.has(s) ? classes.chipActive : ''}`}
            id={activeSources.has(s) ? 'active-tab' : ''}
            label={
              <span>
                <span className={classes.dot} style={{ backgroundColor: SRC[s].color }} />
                {s} 
                {
                  counts[s] ?
                  <span style={{ color: activeSources.has(s) ? '#1D4ED8' : COLORS.textMuted }}>{" "}{counts[s]}</span>
                  : ''
                }
              </span>
            }
          />
        ))}
      </div>
      <div className={classes.row}>
        <div className={classes.searchWrap}>
          <SearchIcon style={{ fontSize: 16, color: COLORS.textMuted }} />
          <InputBase
            className={classes.searchInput}
            placeholder="Search logs by message"
            id="search-input"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {activeSources.has('Simulation') && <Select
            value={logLevel}
            onChange={onLogLevelChange}
            className={classes.select}
            classes={{ select: classes.selectInput }}
            disableUnderline
          >
            {Object.keys(LOG_LEVEL).map(key => (
              <MenuItem value={key}>{LOG_LEVEL[key]}</MenuItem>
            ))}
        </Select>}
        
        <Select
          id="level-select"
          multiple
          value={level}
          onChange={onLevelChange}
          className={classes.select}
          classes={{ select: classes.selectInput }}
          disableUnderline
          displayEmpty
          MenuProps={{
            classes: { paper: classes.menuPaper, list: classes.menuList },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            getContentAnchorEl: null,
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? 'Severity'
              : LEVEL_OPTIONS.filter((opt) => selected.includes(opt.value))
                  .map((opt) => opt.title)
                  .join(', ')
          }
        >
          {LEVEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} className={classes.menuItem}>
              <Checkbox
                checked={level.indexOf(opt.value) > -1}
                className={classes.checkbox}
                size="small"
              />
              <ListItemText primary={opt.title} className={classes.listItemText} />
            </MenuItem>
          ))}
        </Select>
        {showTimeFilter && (
          <LogTimeFilter
            value={time}
            onChange={onTimeChange}
            onReset={() => {
              onTimeChange('all');
            }}
          />
        )}
        {
          activeSources.has('Simulation') &&
          <SimulationRunSelect 
            selectedRunId={selectedSimulationRun}
            setRunId={setSelectedSimulationRun}
            isSimulationRunning={isSimulationRunning}
          />
        }
         {
          isSimulationRunning !== "live" && activeSources.has('Runtime') &&
          <button
            className={classes.terminateButton}
            disabled={clearingRuntimeLogs}
            onClick={handleClearRuntimeLogs}
          >
            {clearingRuntimeLogs ? (
              <>
                <span className={classes.spinner} />
                Clearing Runtime Logs...
              </>
            ) : (
              'Clear Runtime Logs'
            )}
          </button>
        }
        {
          isSimulationRunning === "live" &&
          <button
            className={classes.terminateButton}
            disabled={terminatingSimulation}
            onClick={handleTerminateSimulation}
          >
            {terminatingSimulation ? (
              <>
                <span className={classes.spinner} />
                Terminating Simulation...
              </>
            ) : (
              'Terminate Simulation'
            )}
          </button>
        }
        <IconButton className={classes.iconBtn} id="refresh-button" onClick={onRefresh} aria-label="Refresh">
          <RefreshIcon
            style={{
              fontSize: 18,
              transform: refreshSpin ? 'rotate(360deg)' : 'none',
              transition: 'transform .5s ease',
            }}
          />
        </IconButton>
      </div>
    </div>
  );
}



